<script setup lang="ts">
// Privacy Policy - no auth required
</script>

<template>
  <div class="min-h-screen bg-gray-50 py-8 px-4">
    <div class="max-w-3xl mx-auto bg-white rounded-lg shadow-sm p-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-6">Privacy Policy</h1>
      <p class="text-sm text-gray-500 mb-8">Last updated: January 18, 2026</p>

      <div class="prose prose-gray max-w-none space-y-6">
        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">1. Introduction</h2>
          <p class="text-gray-600">
            Vermont Cabin Calendar ("we", "our", or "us") is a private booking application for
            managing reservations at our family cabin. This privacy policy explains how we collect,
            use, and protect your personal information.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">2. Information We Collect</h2>
          <p class="text-gray-600 mb-2">When you use our service, we collect:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1 ml-4">
            <li><strong>Account Information:</strong> Name, email address, and profile picture from your Google or Facebook account</li>
            <li><strong>Booking Information:</strong> Check-in/check-out dates, number of guests, and any notes you provide</li>
            <li><strong>Usage Data:</strong> Login times and booking history for security purposes</li>
          </ul>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">3. How We Use Your Information</h2>
          <p class="text-gray-600 mb-2">We use your information to:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1 ml-4">
            <li>Manage your cabin reservations</li>
            <li>Send booking confirmations and reminders via email</li>
            <li>Display your name to other authorized users (family members only)</li>
            <li>Prepare the cabin for your arrival (heating, etc.)</li>
          </ul>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">4. Information Sharing</h2>
          <p class="text-gray-600">
            We do not sell or share your personal information with third parties. Your information
            is only visible to:
          </p>
          <ul class="list-disc list-inside text-gray-600 space-y-1 ml-4 mt-2">
            <li>Cabin owners and family members (full booking details)</li>
            <li>Other authorized guests (only see dates as "Reserved")</li>
          </ul>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">5. Data Storage</h2>
          <p class="text-gray-600">
            Your data is stored securely using Cloudflare's infrastructure. We retain your
            account information as long as you maintain an active account. Booking records
            are kept for historical reference and cabin management purposes.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">6. Your Rights</h2>
          <p class="text-gray-600 mb-2">You have the right to:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1 ml-4">
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p class="text-gray-600 mt-2">
            To exercise these rights, visit our <router-link to="/data-deletion" class="text-primary-600 hover:underline">Data Deletion</router-link> page
            or contact us directly.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">7. Cookies</h2>
          <p class="text-gray-600">
            We use essential cookies only to maintain your login session. We do not use
            tracking cookies or analytics services.
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">8. Third-Party Services</h2>
          <p class="text-gray-600">
            We use the following third-party services for authentication:
          </p>
          <ul class="list-disc list-inside text-gray-600 space-y-1 ml-4 mt-2">
            <li>Google Sign-In - <a href="https://policies.google.com/privacy" class="text-primary-600 hover:underline" target="_blank">Google Privacy Policy</a></li>
            <li>Facebook Login - <a href="https://www.facebook.com/privacy/policy" class="text-primary-600 hover:underline" target="_blank">Facebook Privacy Policy</a></li>
          </ul>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">9. Contact Us</h2>
          <p class="text-gray-600">
            If you have questions about this privacy policy or your personal data, please contact us at:
            <a href="mailto:cabin@macris.co" class="text-primary-600 hover:underline">cabin@macris.co</a>
          </p>
        </section>

        <section>
          <h2 class="text-xl font-semibold text-gray-800 mb-3">10. Changes to This Policy</h2>
          <p class="text-gray-600">
            We may update this privacy policy from time to time. We will notify you of any
            changes by posting the new policy on this page and updating the "Last updated" date.
          </p>
        </section>
      </div>

      <div class="mt-8 pt-6 border-t border-gray-200">
        <router-link to="/login" class="text-primary-600 hover:underline">&larr; Back to Login</router-link>
      </div>
    </div>
  </div>
</template>
